/**
 * ListPanelFilterBar
 *
 * 4개 목록 사이드패널(TaskItems / Tasks / TaskBudgets / TaskPerformances) 공용 필터 바.
 *
 * 동작:
 * - "lock" 모드: 부모 페이지에서 해당 필터값을 prop으로 넘겨주면(예: 차트 컨텍스트, 페이지 selector) disabled로 표시.
 *   사용자는 그 값을 바꿀 수 없고, 패널이 닫혔다 다시 열려도 그대로 lock된 상태.
 * - "editable" 모드: prop이 undefined면 패널-로컬 state로 자유 선택. 닫히면 reset(useEffect)으로 비움.
 * - 효과적 필터 = locked prop ?? panel-local state (호출자에서 합성)
 *
 * 성과상태: performanceStatusOptions 를 넘기면 상태 옆에 두 번째 셀렉트로 표시 (세부실행과제 목록 전용).
 *   상태와 AND 조건으로 적용된다.
 *
 * 검색: lock 개념 없음. 부모가 초기값을 넘기지 않고 항상 패널-로컬. 엔터 또는 검색 버튼으로 trigger.
 *
 * 레이아웃:
 * - 각 필드 위에 라벨 표시
 * - 모든 입력 높이 h-10 통일 (BusinessSelect/DepartmentSelect/MemberSelect 기본 높이와 동일)
 */

import React from 'react'
import { Search } from 'lucide-react'
import Button from '@/components/ui/Button'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import { designTokens } from '@/utils/design-tokens'

export interface StatusOption {
  value: string  // '' = 전체
  label: string
}

export interface ListPanelFilterBarProps {
  workspaceId: string

  // ===== 상태 =====
  // showStatus=false면 상태 셀렉트 자체를 숨김 (budget/performance)
  showStatus?: boolean
  statusOptions?: StatusOption[]
  /** lock된 status. 정의되면 disabled 표시 */
  lockedStatus?: string
  /** 패널-로컬 status (editable 모드에서만 의미) */
  status: string
  onStatusChange: (status: string) => void
  /** 상태 셀렉트 라벨. 기본 "상태" */
  statusLabel?: string

  // ===== 성과상태 (선택) — performanceStatusOptions 가 있을 때만 표시 =====
  performanceStatusOptions?: StatusOption[]
  lockedPerformanceStatus?: string
  performanceStatus?: string
  onPerformanceStatusChange?: (status: string) => void

  // ===== 사업 =====
  lockedBusinessId?: string
  lockedBusinessName?: string
  businessId: string
  businessName: string
  onBusinessChange: (id: string, name: string) => void

  // ===== 부서 =====
  lockedDepartmentId?: string
  lockedDepartmentName?: string
  departmentId: string
  departmentName: string
  onDepartmentChange: (id: string, name: string) => void

  // ===== 담당자 =====
  lockedMemberId?: string
  lockedMemberName?: string
  memberId: string
  memberName: string
  onMemberChange: (id: string, name: string, email: string) => void

  // ===== 검색 =====
  searchInput: string
  onSearchInputChange: (value: string) => void
  onSearchSubmit: () => void
}

/** 필터 한 칸 (라벨 + 입력) */
const FilterField: React.FC<{
  label: string
  locked?: boolean
  width: number
  children: React.ReactNode
}> = ({ label, locked, width, children }) => (
  <div className="flex flex-col gap-1" style={{ width }} title={locked ? '클릭한 컨텍스트로 고정됨' : undefined}>
    <div className="flex items-center gap-1">
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-xsmall'][0],
          fontWeight: 600,
          color: designTokens.colors.gray[5],
        }}
      >
        {label}
      </span>
      {locked && (
        <span
          style={{
            fontSize: '10px',
            color: designTokens.colors.gray[4],
            fontFamily: designTokens.typography.fontFamily.sans[0],
          }}
        >
          (고정)
        </span>
      )}
    </div>
    {children}
  </div>
)

/** 상태 셀렉트 한 칸 ('' = 전체) */
const StatusSelectField: React.FC<{
  label: string
  options: StatusOption[]
  value: string
  locked: boolean
  onChange: (value: string) => void
}> = ({ label, options, value, locked, onChange }) => (
  <FilterField label={label} locked={locked} width={150}>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => { if (!locked) onChange(e.target.value) }}
        disabled={locked}
        className="w-full h-10 px-3 border-2 rounded outline-none appearance-none pr-8"
        style={{
          backgroundColor: locked ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color: designTokens.colors.gray[6],
          cursor: locked ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="">전체</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ color: designTokens.colors.gray[4] }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  </FilterField>
)

const ListPanelFilterBar: React.FC<ListPanelFilterBarProps> = ({
  workspaceId,
  showStatus = true,
  statusOptions,
  lockedStatus,
  status,
  onStatusChange,
  statusLabel = '상태',
  performanceStatusOptions,
  lockedPerformanceStatus,
  performanceStatus = '',
  onPerformanceStatusChange,
  lockedBusinessId,
  lockedBusinessName,
  businessId,
  businessName,
  onBusinessChange,
  lockedDepartmentId,
  lockedDepartmentName,
  departmentId,
  departmentName,
  onDepartmentChange,
  lockedMemberId,
  lockedMemberName,
  memberId,
  memberName,
  onMemberChange,
  searchInput,
  onSearchInputChange,
  onSearchSubmit,
}) => {
  const isStatusLocked = lockedStatus !== undefined
  const isPerformanceStatusLocked = lockedPerformanceStatus !== undefined
  const isBusinessLocked = lockedBusinessId !== undefined
  const isDepartmentLocked = lockedDepartmentId !== undefined
  const isMemberLocked = lockedMemberId !== undefined

  const displayStatus = isStatusLocked ? (lockedStatus || '') : status
  const displayPerformanceStatus = isPerformanceStatusLocked ? (lockedPerformanceStatus || '') : performanceStatus
  const displayBusinessId = isBusinessLocked ? (lockedBusinessId || null) : (businessId || null)
  const displayBusinessName = isBusinessLocked ? (lockedBusinessName || lockedBusinessId || null) : (businessName || null)
  const displayDepartmentId = isDepartmentLocked ? (lockedDepartmentId || null) : (departmentId || null)
  const displayDepartmentName = isDepartmentLocked ? (lockedDepartmentName || lockedDepartmentId || null) : (departmentName || null)
  const displayMemberId = isMemberLocked ? (lockedMemberId || null) : (memberId || null)
  const displayMemberName = isMemberLocked ? (lockedMemberName || lockedMemberId || null) : (memberName || null)

  return (
    <div
      className="flex flex-col gap-3 px-4 py-3"
      style={{
        borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
        backgroundColor: designTokens.colors.gray[0],
      }}
    >
      {/* Row 1: 상태/(성과상태)/사업/부서/담당자 */}
      <div className="flex flex-wrap items-end gap-3">
        {/* 상태 */}
        {showStatus && statusOptions && (
          <StatusSelectField
            label={statusLabel}
            options={statusOptions}
            value={displayStatus}
            locked={isStatusLocked}
            onChange={onStatusChange}
          />
        )}

        {/* 성과상태 */}
        {performanceStatusOptions && (
          <StatusSelectField
            label="성과상태"
            options={performanceStatusOptions}
            value={displayPerformanceStatus}
            locked={isPerformanceStatusLocked}
            onChange={(v) => onPerformanceStatusChange?.(v)}
          />
        )}

        {/* 사업 */}
        <FilterField label="사업" locked={isBusinessLocked} width={180}>
          <BusinessSelect
            workspaceId={workspaceId}
            selectedBusinessId={displayBusinessId}
            selectedBusinessName={displayBusinessName}
            onSelect={(id, name) => { if (!isBusinessLocked) onBusinessChange(id, name) }}
            disabled={isBusinessLocked}
            placeholder="사업 전체"
            showAllOption
          />
        </FilterField>

        {/* 부서 */}
        <FilterField label="부서" locked={isDepartmentLocked} width={180}>
          <DepartmentSelect
            workspaceId={workspaceId}
            selectedDepartmentId={displayDepartmentId}
            selectedDepartmentName={displayDepartmentName}
            onSelect={(id, name) => { if (!isDepartmentLocked) onDepartmentChange(id, name) }}
            disabled={isDepartmentLocked}
            placeholder="부서 전체"
            showAllOption
          />
        </FilterField>

        {/* 담당자 */}
        <FilterField label="담당자" locked={isMemberLocked} width={180}>
          <MemberSelect
            workspaceId={workspaceId}
            label="담당자"
            selectedMemberId={displayMemberId}
            selectedMemberName={displayMemberName}
            onSelect={(id, name, email) => { if (!isMemberLocked) onMemberChange(id, name, email) }}
            disabled={isMemberLocked}
            placeholder="담당자 전체"
            showAllOption
          />
        </FilterField>
      </div>

      {/* Row 2: 검색 input + 버튼 — 항상 두 번째 줄 */}
      <div className="flex items-end gap-2">
        <FilterField label="검색" width={300}>
          <div
            className="flex items-center gap-1.5 px-3 h-10 border-2 rounded"
            style={{ backgroundColor: designTokens.colors.white, borderColor: designTokens.colors.gray[1] }}
          >
            <Search className="w-4 h-4 flex-shrink-0" style={{ color: designTokens.colors.gray[3] }} />
            <input
              type="text"
              placeholder="명칭으로 검색"
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSearchSubmit() }}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                color: searchInput ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
              }}
            />
          </div>
        </FilterField>
        <Button color="dark" size="medium" onClick={onSearchSubmit} className="h-10 px-4">
          검색
        </Button>
      </div>
    </div>
  )
}

export default ListPanelFilterBar
